import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Loader2, Sparkles, Check, ChevronLeft, ChevronRight, X, Plus, Trash2, ImagePlus,
  Target, Users as UsersIcon, ListTree, Cpu, MessageSquare, Building2, GripVertical,
} from "lucide-react";
import { toast } from "sonner";
import usePermissions from "@/lib/usePermissions";
import { PROJECT_CATEGORIES } from "@/lib/projectCategory";

const STEPS = [
  { id: "basics",    label: "Basics",     icon: Building2, hint: "Name, budget, client, dates" },
  { id: "scope",     label: "Scope",      icon: Target,    hint: "Goals, problems, outcomes" },
  { id: "team",      label: "Team",       icon: UsersIcon, hint: "Assign people + roles" },
  { id: "ms",        label: "Milestones", icon: ListTree,  hint: "Dates, owners, dependencies" },
  { id: "hardware",  label: "Hardware",   icon: Cpu,       hint: "Optional BOM/DFM scaffold" },
  { id: "comms",     label: "Comms",      icon: MessageSquare, hint: "Channel + kickoff" },
];

// Defined at module scope so React doesn't see a fresh component type per render
// of ProjectWizard — which would otherwise blow away the input's local `draft`
// state and focus on every keystroke.
function ScopeListEditor({ label, list, onChange, max, testId }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (max && list.length >= max) { toast.error(`Max ${max}`); return; }
    onChange([...list, v]);
    setDraft("");
  };
  return (
    <div data-testid={`wiz-scope-${testId}`}>
      <Label className="label-overline">{label}{max ? ` (max ${max})` : ""}</Label>
      <div className="flex items-center gap-2 mt-1">
        <Input className="rounded-sm flex-1" value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
        <Button size="sm" variant="outline" className="rounded-sm" onClick={add}><Plus size={11} /></Button>
      </div>
      {list.length > 0 && (
        <ul className="mt-2 space-y-1">
          {list.map((it, i) => (
            <li key={`${it}-${i}`} className="flex items-center justify-between gap-2 px-2 py-1 bg-slate-50 rounded-sm">
              <span className="text-xs text-slate-700">{it}</span>
              <button onClick={() => onChange(list.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600">
                <X size={11} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const DEFAULT_MS = [
  { title: "Discovery & Research" },
  { title: "Concept Sketches" },
  { title: "CAD & Engineering" },
  { title: "Prototype v1" },
  { title: "DFM & Tooling" },
];

const blankProject = () => ({
  name: "", business_unit: "BU1", category: "client_delivery", client_name: "", client_user_id: "",
  description: "", status: "active",
  start_date: new Date().toISOString().slice(0, 10),
  end_date: "",
  budget: "", currency: "INR",
  cover_image: "",
  team_members: [],
  goals: [],
  problems: [],
  outcomes: [],
  physical_product: false,
  client_can_view: true,
  kickoff_meeting: null,
  daily_task_sync: false,  // Phase 77 — admin-only opt-in
  default_source_id: "",  // Feb 2026 — "Cash Fix": Default Bank Account
});

const today = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, days) => {
  const d = iso ? new Date(iso) : new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export default function ProjectWizard({ open, onOpenChange, onCreated }) {
  const can = usePermissions();
  const isAdmin = can("projects_create");
  const [step, setStep] = useState(0);
  const [proj, setProj] = useState(blankProject);
  const [clients, setClients] = useState([]);
  const [team, setTeam] = useState([]);  // pool of selectable employees
  const [financeSources, setFinanceSources] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [saving, setSaving] = useState(false);
  const [aiThinking, setAiThinking] = useState(false);

  // Reset when reopened
  useEffect(() => {
    if (open) {
      setStep(0);
      setProj(blankProject());
      setMilestones(DEFAULT_MS.map((m, i) => ({
        temp_key: `ms-${i}`,
        title: m.title,
        due_date: addDays(today(), 21 * (i + 1)),
        start_date: i === 0 ? today() : "",
        status: "planned",
        assignee_id: "",
        order: i,
        depends_on: i === 0 ? [] : [`ms-${i - 1}`],
      })));
    }
  }, [open]);

  // Load clients + team
  useEffect(() => {
    if (!open) return;
    api.get("/admin/clients").then(({ data }) => setClients(data)).catch(() => {});
    api.get("/users", { params: { kind: "internal" } }).then(({ data }) => setTeam((data || []).filter((u) => u.status === "active"))).catch(() => {});
    // Feb 2026 — "Cash Fix": Default Bank Account dropdown source list.
    api.get("/finance/sources").then(({ data }) => setFinanceSources(data || [])).catch(() => {});
  }, [open]);

  const setField = (k, v) => setProj((p) => ({ ...p, [k]: v }));

  // ---- step validation ----
  const validateStep = () => {
    if (step === 0) {
      if (!proj.name.trim()) return "Project name required";
      if (!proj.business_unit) return "BU required";
      if (!proj.category) return "Pick a project category";
      if (!proj.client_user_id) return "Pick a client from the Registry";
    }
    if (step === 3) {
      if (!milestones.length) return "At least one milestone required";
      // No self-dependencies + no cycles via topological check
      const seen = new Set();
      const visiting = new Set();
      const adj = Object.fromEntries(milestones.map((m) => [m.temp_key, m.depends_on || []]));
      const hasCycle = (k) => {
        if (visiting.has(k)) return true;
        if (seen.has(k)) return false;
        visiting.add(k);
        for (const d of adj[k] || []) if (hasCycle(d)) return true;
        visiting.delete(k); seen.add(k);
        return false;
      };
      for (const m of milestones) if (hasCycle(m.temp_key)) return `Cycle detected in milestone dependencies (${m.title})`;
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const aiDescribe = async () => {
    if (!proj.name.trim()) { toast.error("Add a name first"); return; }
    setAiThinking(true);
    try {
      const { data } = await api.post("/ai/project-description", {
        name: proj.name, client_name: proj.client_name, business_unit: proj.business_unit,
        seed: proj.description,
      });
      setField("description", data.description || "");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAiThinking(false); }
  };

  // ---- submit ----
  const submit = async () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const payload = {
        project: {
          ...proj,
          budget: proj.budget ? Number(proj.budget) : null,
          team_members: proj.team_members.map((m) => ({ user_id: m.user_id, project_role: m.project_role || "contributor" })),
        },
        milestones: milestones.map((m) => ({
          title: m.title,
          due_date: m.due_date || null,
          start_date: m.start_date || null,
          assignee_id: m.assignee_id || null,
          status: m.status || "planned",
          order: m.order,
          temp_key: m.temp_key,
          depends_on: m.depends_on || [],
        })),
      };
      const { data } = await api.post("/projects/wizard", payload);
      toast.success(`Project "${data.name}" created.`);
      onOpenChange(false);
      onCreated && onCreated(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // ---- step bodies ----
  const StepBasics = (
    <div className="space-y-4" data-testid="wiz-step-basics">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="label-overline">Project name *</Label>
          <Input className="rounded-sm mt-1" autoFocus value={proj.name}
            onChange={(e) => setField("name", e.target.value)}
            placeholder="e.g. Northstar Smart Bottle"
            data-testid="wiz-name" />
        </div>
        <div>
          <Label className="label-overline">Business unit *</Label>
          <Select value={proj.business_unit} onValueChange={(v) => setField("business_unit", v)}>
            <SelectTrigger className="rounded-sm mt-1" data-testid="wiz-bu"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="BU1">BU1 — Hardware</SelectItem>
              <SelectItem value="BU2">BU2 — Digital</SelectItem>
              <SelectItem value="BU3">BU3 — Services</SelectItem>
              <SelectItem value="Operations">Operations — Internal</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <Label className="label-overline">Status</Label>
          <Select value={proj.status} onValueChange={(v) => setField("status", v)}>
            <SelectTrigger className="rounded-sm mt-1" data-testid="wiz-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="on_hold">On hold</SelectItem>
              <SelectItem value="delayed">Delayed</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="out_of_scope">Out of scope</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-overline">Category *</Label>
          <Select value={proj.category} onValueChange={(v) => setField("category", v)}>
            <SelectTrigger className="rounded-sm mt-1" data-testid="wiz-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PROJECT_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-overline">Client *</Label>
          <Select value={proj.client_user_id || ""} onValueChange={(v) => {
            const c = clients.find((x) => x.id === v);
            setField("client_user_id", v);
            setField("client_name", c?.company || c?.name || "");
          }}>
            <SelectTrigger className="rounded-sm mt-1" data-testid="wiz-client"><SelectValue placeholder="Pick a client from Registry" /></SelectTrigger>
            <SelectContent>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-slate-400 mt-1">
            Client not listed? Add them in <a href="/admin/registry" className="text-[#F54800] hover:underline" data-testid="wiz-client-registry-link">Registry</a> first, then come back and select.
          </p>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label className="label-overline">Description</Label>
          <Button variant="ghost" size="sm" className="rounded-sm h-7 text-[#F54800] hover:bg-[#F54800]/5" onClick={aiDescribe} disabled={aiThinking} data-testid="wiz-ai-describe">
            {aiThinking ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Sparkles size={12} className="mr-1.5" />}
            AI generate
          </Button>
        </div>
        <Textarea rows={4} className="rounded-sm mt-1 resize-none" value={proj.description || ""}
          placeholder="One paragraph for the team and the client."
          onChange={(e) => setField("description", e.target.value)} data-testid="wiz-description" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <Label className="label-overline">Start date</Label>
          <Input type="date" className="rounded-sm mt-1" value={proj.start_date || ""}
            onChange={(e) => setField("start_date", e.target.value)} data-testid="wiz-start" />
        </div>
        <div>
          <Label className="label-overline">End date</Label>
          <Input type="date" className="rounded-sm mt-1" value={proj.end_date || ""}
            onChange={(e) => setField("end_date", e.target.value)} data-testid="wiz-end" />
        </div>
        <div>
          <Label className="label-overline">Budget</Label>
          <div className="flex items-stretch gap-1 mt-1">
            <Select value={proj.currency} onValueChange={(v) => setField("currency", v)}>
              <SelectTrigger className="rounded-sm w-20" data-testid="wiz-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="INR">₹ INR</SelectItem>
                <SelectItem value="USD">$ USD</SelectItem>
              </SelectContent>
            </Select>
            <Input className="rounded-sm flex-1" type="number" min="0" value={proj.budget}
              onChange={(e) => setField("budget", e.target.value)} placeholder="0" data-testid="wiz-budget" />
          </div>
        </div>
      </div>

      <div>
        <Label className="label-overline">Cover image URL (optional)</Label>
        <div className="flex items-center gap-2 mt-1">
          <ImagePlus size={14} className="text-slate-400" />
          <Input className="rounded-sm flex-1" placeholder="https://…" value={proj.cover_image || ""}
            onChange={(e) => setField("cover_image", e.target.value)} data-testid="wiz-cover" />
        </div>
      </div>

      {/* Phase 77 — admin-only toggle: opt this project into daily-task sync */}
      {isAdmin && (
        <label className="flex items-start gap-2 text-xs text-slate-700" data-testid="project-form-daily-task-sync">
          <input
            type="checkbox"
            checked={!!proj.daily_task_sync}
            onChange={(e) => setField("daily_task_sync", e.target.checked)}
            className="mt-0.5 accent-[#F54800]"
            data-testid="project-form-daily-task-sync-input"
          />
          <span>
            <span className="font-medium">Enable daily-task sync</span>
            <span className="block text-[11px] text-slate-500">Lets the team attach quick daily tasks + log time from the dashboard widget to this project.</span>
          </span>
        </label>
      )}

      {/* Feb 2026 — "Cash Fix": which bank account this project's revenue
          lands in by default, used by the source_id backfill + any future
          auto-assignment of source_id on new invoices. */}
      {isAdmin && (
        <div data-testid="project-form-default-source">
          <Label className="label-overline">Default Bank Account</Label>
          <Select
            value={proj.default_source_id || "none"}
            onValueChange={(v) => setField("default_source_id", v === "none" ? "" : v)}
          >
            <SelectTrigger className="rounded-sm mt-1" data-testid="project-form-default-source-trigger">
              <SelectValue placeholder="Not set" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Not set</SelectItem>
              {financeSources.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-slate-500 mt-1">Where this project's revenue is deposited. Used to auto-tag incoming invoices for accurate bank balances.</p>
        </div>
      )}
    </div>
  );

  // ---- Scope step ----
  const StepScope = (
    <div className="space-y-4" data-testid="wiz-step-scope">
      <ScopeListEditor label="Goals" list={proj.goals} onChange={(l) => setField("goals", l)} testId="goals" />
      <ScopeListEditor label="Problems" list={proj.problems} onChange={(l) => setField("problems", l)} max={3} testId="problems" />
      <ScopeListEditor label="Expected outcomes" list={proj.outcomes} onChange={(l) => setField("outcomes", l)} testId="outcomes" />
    </div>
  );

  // ---- Team step ----
  const toggleTeamMember = (uid) => {
    setProj((p) => {
      const exists = (p.team_members || []).find((m) => m.user_id === uid);
      return {
        ...p,
        team_members: exists
          ? p.team_members.filter((m) => m.user_id !== uid)
          : [...p.team_members, { user_id: uid, project_role: "contributor" }],
      };
    });
  };

  const setMemberRole = (uid, role) => {
    setProj((p) => ({
      ...p,
      team_members: p.team_members.map((m) => m.user_id === uid ? { ...m, project_role: role } : m),
    }));
  };

  const StepTeam = (
    <div className="space-y-3" data-testid="wiz-step-team">
      <p className="text-xs text-slate-500">Pick the people working on this project. One person per project_role recommended.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[400px] overflow-y-auto pr-1">
        {team.map((u) => {
          const member = proj.team_members.find((m) => m.user_id === u.id);
          const picked = !!member;
          return (
            <div key={u.id} className={`rounded-sm border-2 p-2.5 transition-colors ${picked ? "border-[#F54800] bg-[#F54800]/5" : "border-slate-200 hover:border-slate-300"}`}>
              <button onClick={() => toggleTeamMember(u.id)} className="w-full text-left flex items-center gap-2" data-testid={`wiz-team-toggle-${u.id}`}>
                <div className={`w-5 h-5 rounded-sm flex items-center justify-center shrink-0 ${picked ? "bg-[#F54800] text-white" : "bg-slate-100 text-slate-400"}`}>
                  {picked && <Check size={12} />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-slate-900 truncate">{u.name}</div>
                  <div className="text-[10px] text-slate-500 truncate">{(u.role || "").replace(/_/g, " ")} · {u.business_unit || "—"}</div>
                </div>
              </button>
              {picked && (
                <div className="mt-2">
                  <Select value={member.project_role} onValueChange={(v) => setMemberRole(u.id, v)}>
                    <SelectTrigger className="rounded-sm h-7 text-[11px]" data-testid={`wiz-team-role-${u.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lead">Lead</SelectItem>
                      <SelectItem value="contributor">Contributor</SelectItem>
                      <SelectItem value="reviewer">Reviewer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="text-[11px] text-slate-500" data-testid="wiz-team-count">
        {proj.team_members.length} member{proj.team_members.length === 1 ? "" : "s"} selected
      </div>
    </div>
  );

  // ---- Milestone step ----
  const updateMs = (i, patch) => setMilestones((arr) => arr.map((m, j) => j === i ? { ...m, ...patch } : m));
  const addMs = () => {
    const i = milestones.length;
    setMilestones((arr) => [...arr, {
      temp_key: `ms-${i}-${Date.now()}`,
      title: "New milestone",
      due_date: addDays(arr.length ? arr[arr.length - 1].due_date : today(), 21),
      start_date: "",
      status: "planned",
      assignee_id: "",
      order: i,
      depends_on: arr.length ? [arr[arr.length - 1].temp_key] : [],
    }]);
  };
  const delMs = (i) => setMilestones((arr) => arr.filter((_, j) => j !== i).map((m, k) => ({ ...m, order: k })));

  const toggleDep = (i, depKey) => updateMs(i, {
    depends_on: (milestones[i].depends_on || []).includes(depKey)
      ? milestones[i].depends_on.filter((d) => d !== depKey)
      : [...(milestones[i].depends_on || []), depKey],
  });

  const StepMilestones = (
    <div className="space-y-3" data-testid="wiz-step-ms">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">Pre-filled with the 5 defaults — edit titles, dates, owners, dependencies.</p>
        <Button variant="outline" size="sm" className="rounded-sm" onClick={addMs} data-testid="wiz-ms-add"><Plus size={11} className="mr-1" />Add milestone</Button>
      </div>
      <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
        {milestones.map((m, i) => (
          <div key={m.temp_key} className="card-flat p-3" data-testid={`wiz-ms-row-${i}`}>
            <div className="flex items-start gap-2">
              <GripVertical size={14} className="text-slate-300 mt-2 shrink-0" />
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-12 gap-2">
                <Input className="rounded-sm sm:col-span-4" placeholder="Title" value={m.title}
                  onChange={(e) => updateMs(i, { title: e.target.value })}
                  data-testid={`wiz-ms-title-${i}`} />
                <Input type="date" className="rounded-sm sm:col-span-3" value={m.due_date || ""}
                  onChange={(e) => updateMs(i, { due_date: e.target.value })}
                  data-testid={`wiz-ms-due-${i}`} />
                <Select value={m.assignee_id || "__none"} onValueChange={(v) => updateMs(i, { assignee_id: v === "__none" ? "" : v })}>
                  <SelectTrigger className="rounded-sm sm:col-span-3" data-testid={`wiz-ms-assignee-${i}`}><SelectValue placeholder="Assignee" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">Unassigned</SelectItem>
                    {team.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <button onClick={() => delMs(i)} className="sm:col-span-2 text-slate-400 hover:text-red-600 self-center text-xs" data-testid={`wiz-ms-del-${i}`}>
                  <Trash2 size={12} className="inline mr-1" />Remove
                </button>
                {/* Dependencies */}
                {i > 0 && (
                  <div className="sm:col-span-12">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1 mb-1">Depends on</div>
                    <div className="flex flex-wrap gap-1">
                      {milestones.slice(0, i).map((earlier) => {
                        const picked = (m.depends_on || []).includes(earlier.temp_key);
                        return (
                          <button
                            key={earlier.temp_key}
                            onClick={() => toggleDep(i, earlier.temp_key)}
                            className={`text-[10px] px-2 py-0.5 rounded-sm border ${picked ? "bg-[#F54800] text-white border-[#F54800]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
                            data-testid={`wiz-ms-dep-${i}-${earlier.order}`}
                          >
                            {earlier.title || "(untitled)"}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // ---- Hardware step ----
  const StepHardware = (
    <div className="space-y-4" data-testid="wiz-step-hardware">
      <label className="flex items-start gap-3 p-3 rounded-sm border-2 border-slate-200 cursor-pointer hover:border-slate-300 transition-colors"
        style={proj.physical_product ? { borderColor: "#F54800", background: "rgba(245, 72, 0, 0.05)" } : {}}>
        <input
          type="checkbox" checked={!!proj.physical_product}
          onChange={(e) => setField("physical_product", e.target.checked)}
          className="mt-0.5"
          data-testid="wiz-physical-toggle"
        />
        <div>
          <div className="font-display font-bold text-slate-900 text-sm">Physical product</div>
          <p className="text-[11px] text-slate-600 mt-1 leading-relaxed">
            Initialises an empty <b>BOM</b>, a <b>Vendor / Tooling / DFM tracker</b>, and a <b>Prototype iteration log</b> on the project page.
            Leave unchecked for purely-digital deliverables — you can flip this on later.
          </p>
        </div>
      </label>
      {proj.physical_product && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-[11px] text-amber-900" data-testid="wiz-hardware-info">
          <Cpu size={12} className="inline mr-1 mb-0.5" />
          Hardware tabs will appear on the project page after creation. They start empty — your team fills them in during Discovery + DFM.
        </div>
      )}
    </div>
  );

  // ---- Comms step ----
  const StepComms = (
    <div className="space-y-4" data-testid="wiz-step-comms">
      <div className="card-flat p-3">
        <div className="flex items-start gap-2">
          <MessageSquare size={14} className="text-[#F54800] mt-0.5" />
          <div className="flex-1">
            <div className="font-display font-bold text-sm text-slate-900">One project channel</div>
            <p className="text-[11px] text-slate-500 mt-1">Created automatically. Selected team + the linked client all join. We never duplicate channels.</p>
          </div>
        </div>
      </div>

      <label className="flex items-start gap-3 p-3 rounded-sm border-2 border-slate-200 cursor-pointer hover:border-slate-300 transition-colors"
        style={proj.client_can_view ? { borderColor: "#F54800", background: "rgba(245, 72, 0, 0.05)" } : {}}>
        <input type="checkbox" checked={!!proj.client_can_view} onChange={(e) => setField("client_can_view", e.target.checked)} className="mt-0.5" data-testid="wiz-client-view" />
        <div>
          <div className="font-display font-bold text-slate-900 text-sm">Client can view the project</div>
          <p className="text-[11px] text-slate-600 mt-1">Linked client gets read access via their portal — milestones, updates, weekly reports.</p>
        </div>
      </label>

      <div>
        <Label className="label-overline">Optional kickoff meeting</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
          <Input type="datetime-local" className="rounded-sm" value={proj.kickoff_meeting?.when || ""}
            onChange={(e) => setField("kickoff_meeting", { ...(proj.kickoff_meeting || {}), when: e.target.value })}
            data-testid="wiz-kickoff-when" />
          <Input className="rounded-sm" placeholder="Meet.new link / venue" value={proj.kickoff_meeting?.where || ""}
            onChange={(e) => setField("kickoff_meeting", { ...(proj.kickoff_meeting || {}), where: e.target.value })}
            data-testid="wiz-kickoff-where" />
        </div>
        <Textarea rows={2} className="rounded-sm mt-2 resize-none" placeholder="Notes / agenda…"
          value={proj.kickoff_meeting?.notes || ""}
          onChange={(e) => setField("kickoff_meeting", { ...(proj.kickoff_meeting || {}), notes: e.target.value })}
          data-testid="wiz-kickoff-notes" />
      </div>
    </div>
  );

  const body = useMemo(() => {
    switch (STEPS[step].id) {
      case "basics": return StepBasics;
      case "scope": return StepScope;
      case "team": return StepTeam;
      case "ms": return StepMilestones;
      case "hardware": return StepHardware;
      case "comms": return StepComms;
      default: return null;
    }
  }, [step, proj, milestones, team, clients, aiThinking]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-3xl max-h-[92vh] flex flex-col p-0" data-testid="project-wizard">
        <DialogHeader className="p-5 border-b border-slate-100 shrink-0">
          <DialogTitle className="font-display text-lg">Create new project · {STEPS[step].label}</DialogTitle>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-1 px-4 py-2 border-b border-slate-100 overflow-x-auto shrink-0" data-testid="wiz-stepper">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const active = i === step;
            const done = i < step;
            return (
              <button
                key={s.id}
                onClick={() => i <= step && setStep(i)}
                className={`inline-flex items-center gap-1.5 px-2 py-1.5 rounded-sm text-[11px] font-semibold whitespace-nowrap transition-colors ${
                  active ? "bg-slate-900 text-white" : done ? "text-[#F54800] hover:bg-slate-50" : "text-slate-400"
                }`}
                data-testid={`wiz-step-btn-${s.id}`}
              >
                {done ? <Check size={11} /> : <Icon size={11} />}
                <span>{s.label}</span>
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto p-5">
          <p className="text-[11px] text-slate-500 mb-4">{STEPS[step].hint}</p>
          {body}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-slate-100 shrink-0">
          <Button variant="ghost" onClick={back} disabled={step === 0} className="rounded-sm" data-testid="wiz-back">
            <ChevronLeft size={13} className="mr-1" /> Back
          </Button>
          <div className="flex items-center gap-2">
            {step < STEPS.length - 1 ? (
              <Button onClick={next} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="wiz-next">
                Next <ChevronRight size={13} className="ml-1" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={saving} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="wiz-submit">
                {saving ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Check size={13} className="mr-1.5" />}
                Create project
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
